<template>
  <div class="home">
    <v-container fluid class="pa-0 mobile-padding">
      <!-- Hero Section -->
      <HeroSection />

      <!-- Upload Section -->
      <UploadSection />

      <!-- Analysis Journey Section -->
      <AnalysisJourneySection />

      <!-- How It Works Section -->
      <HowItWorksSection />

      <!-- Testimonials Section -->
      <!--TestimonialsSection /-->

      <!-- Pricing Section -->
      <PricingSection />
      
      <!-- FAQ Section -->
      <FAQSection />
    </v-container>
  </div>
</template>

<script setup>
import { watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { wantsPricing } from '@/router/pricingIntent'
import FAQSection from '@/components/home/FAQSection.vue'
import AnalysisJourneySection from '@/components/home/AnalysisJourneySection.vue'
import HeroSection from '@/components/home/HeroSection.vue'
import UploadSection from '@/components/home/UploadSection.vue'
import HowItWorksSection from '@/components/home/HowItWorksSection.vue'
import PricingSection from '@/components/home/PricingSection.vue'
// import TestimonialsSection from '@/components/home/TestimonialsSection.vue'

// Redirect signed-in users away from the marketing landing.
// router.beforeEach handles the case where auth is ready before
// navigation resolves; this watcher catches the slower case where
// Firebase's onAuthStateChanged restores the session AFTER Home
// has already mounted (browser refresh on /).
const router = useRouter()
const authStore = useAuthStore()

// Capture the pricing intent ONCE, synchronously at setup — before
// PricingSection's onMounted consumes ?pricing=1 and rewrites the URL to a
// clean "/". If we re-read the live route inside redirectIfAuthenticated, the
// query may already have been stripped by the time the auth check runs (e.g.
// the account-rail "Buy credits" SPA navigation), so we'd wrongly bounce the
// user to the dashboard.
//
// We OR two sources because `router.currentRoute.value` can still lag one tick
// behind during an in-app push (it reads the previous route at the moment
// Home's setup runs), whereas the browser URL is already committed via
// pushState. Checking both makes the detection robust to that lag.
function detectPricingIntentFromLocation() {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('pricing') === '1' || window.location.hash === '#pricing'
}
const arrivedForPricing = wantsPricing(router.currentRoute.value)
  || detectPricingIntentFromLocation()

const redirectIfAuthenticated = () => {
  // Honour an explicit pricing intent — the purchase flow lives on this
  // landing, so a signed-in user who asked for pricing must be allowed to
  // stay. Mirrors the router guard.
  if (arrivedForPricing) return
  if (authStore.isAuthenticated) {
    router.replace({ name: 'AnalysesDashboard' })
  }
}

onMounted(redirectIfAuthenticated)
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) redirectIfAuthenticated()
})
</script>

<style scoped>
/* Add responsive padding adjustments */
@media (max-width: 600px) {
  .mobile-padding :deep(section) {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }
  
  /* Adjust spacing between sections */
  .mobile-padding :deep(section + section) {
    margin-top: 1rem !important;
  }
}
</style>
